import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable, map } from 'rxjs';
import { PermissionStateFacade } from '../../shared/store/permission-state/permission-state.facade';
import { FeaturesEnum } from '../models/enums/features.enum';
import { FeaturePermissions } from '../feature-permission.map';

@Injectable({
  providedIn: 'root'
})
export class AuthorizationGuard {

  constructor(
    private readonly permissionStateFacade: PermissionStateFacade,
    private readonly router: Router
  ) { }

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {

    const feature = this.getFeatureFromRoute(route, state);

    if (!feature) {
      this.router.navigate(['/unauthorized']);
      return new Observable(obs => { obs.next(false); obs.complete(); });
    }

    const moduleAccessPermission = FeaturePermissions[feature]?.MODULE_ACCESS;
    if (!moduleAccessPermission) {
      this.router.navigate(['/unauthorized']);
      return new Observable(obs => { obs.next(false); obs.complete(); });
    }

    return this.permissionStateFacade.hasPermission(feature, moduleAccessPermission).pipe(
      map(hasAccess => {
        if (hasAccess) {
          return true;
        }
        this.router.navigate(['/unauthorized']);
        return false;
      })
    );
  }

  private getFeatureFromRoute(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): FeaturesEnum | undefined {
    const routePath = route.routeConfig?.path ?? '';
    const routePathSegments = routePath.split('/').filter(s => s.length > 0);
    const staticRouteSegment = routePathSegments.find(s => !s.startsWith(':'));

    if (staticRouteSegment) {
      const featureFromRoutePath = this.getFeatureFromSegment(staticRouteSegment);
      if (featureFromRoutePath) {
        return featureFromRoutePath;
      }
    }

    // Fallback: scan URL segments from right to left and pick the first known feature segment.
    const urlSegments = state.url.split('?')[0].split('/').filter(s => s.length > 0);
    for (let i = urlSegments.length - 1; i >= 0; i--) {
      const featureFromUrl = this.getFeatureFromSegment(urlSegments[i]);
      if (featureFromUrl) {
        return featureFromUrl;
      }
    }

    return undefined;
  }

  private getFeatureFromSegment(segment: string): FeaturesEnum | undefined {
    switch (segment) {
      case 'home':
        return FeaturesEnum.HOME;
      case 'dashboard':
        return FeaturesEnum.PTA_EXISTING_REQUESTS;
      case 'newptaforms':
        return FeaturesEnum.PTA_NEW_REQUEST;
      case 'premiumchecklist':
        return FeaturesEnum.PREMIUM_CHECKLIST;
      case 'lps-records':
        return FeaturesEnum.UPDATE_LPS_ROUTES;
      case 'update-lps-records':
        return FeaturesEnum.CONFIRM_LPS_ROUTES;
      case 'supplier-dashboard':
        return FeaturesEnum.SUPPLIER_DASHBOARD;
      case 'carrier-dashboard':
        return FeaturesEnum.CARRIER_DASHBOARD;
      case 'engineering-dashboard':
        return FeaturesEnum.ENGINEERING_DASHBOARD;
      case 'usermanagement':
        return FeaturesEnum.USER_MANAGEMENT;
      case 'lookups':
        return FeaturesEnum.LOOKUPS_MANAGEMENT;
      case 'signoff-contacts':
        return FeaturesEnum.SIGNOFF_CONTACTS;
      case 'ptaconfig':
        return FeaturesEnum.PTA_CONFIG;
      case 'role-permissions':
        return FeaturesEnum.ROLE_PERMISSIONS;
      case 'ticket-dashboard':
        return FeaturesEnum.TICKETS_DASHBOARD;
      case 'ticket-queue':
        return FeaturesEnum.TICKET_QUEUE;
      case 'log-ticket':
        return FeaturesEnum.LOG_TICKET;
      case 'test-cases':
        return FeaturesEnum.TEST_CASES;
      default:
        return undefined;
    }
  }
}

